import { useEffect, useState } from "react";

import { useVersionCheck } from "@/hooks/useVersionCheck";
import ButtonV2 from "@/v2/Buttons";

import Box from "./Box";
import Text from "./Text";

const COUNTDOWN_SECONDS = 10;
const LAST_REFRESH_KEY = "omnos_last_auto_refresh";

export default function VersionMismatchBanner() {
  const { versionMismatch, backendVersion, frontendVersion, canAutoRefresh } = useVersionCheck();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [hasStartedCountdown, setHasStartedCountdown] = useState(false);

  useEffect(() => {
    if (versionMismatch && canAutoRefresh && !hasStartedCountdown) {
      setHasStartedCountdown(true);
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [versionMismatch, canAutoRefresh, hasStartedCountdown]);

  useEffect(() => {
    if (!hasStartedCountdown || !canAutoRefresh) {
      return;
    }

    if (countdown <= 0) {
      handleRefresh();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, hasStartedCountdown, canAutoRefresh]);

  const handleRefresh = () => {
    try {
      localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
    } catch (e) {
      console.warn("Could not store refresh timestamp:", e);
    }
    window.location.reload();
  };

  if (!versionMismatch) {
    return null;
  }

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bg="#000000"
      color="white"
      py={4}
      px={4}
      zIndex={9999}
      boxShadow="0 -2px 20px rgba(0,0,0,0.3)"
      data-component-name="VersionMismatchBanner"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" maxWidth={1200} mx="auto">
        <Box>
          <Text fontSize={[14, 15, 16]} fontWeight={500} lineHeight="1.5" color="white">
            A new version of the Regenerus platform is available.
          </Text>
          <Text fontSize={[14, 15, 16]} fontWeight={500} lineHeight="1.5" color="white" mt={1}>
            {canAutoRefresh
              ? `Refreshing in ${countdown} second${countdown !== 1 ? "s" : ""}...`
              : "Please refresh for the latest updates."}
          </Text>
        </Box>
        <Box ml={4} flexShrink={0}>
          <ButtonV2
            onClick={handleRefresh}
            variant="solid"
            size="sm"
            sx={{
              backgroundColor: "white",
              color: "#000000",
              fontWeight: 600,
              minWidth: "120px",
              "&:hover": {
                backgroundColor: "#e0e0e0"
              }
            }}
          >
            {canAutoRefresh ? "Refresh Now" : "Refresh"}
          </ButtonV2>
        </Box>
      </Box>
    </Box>
  );
}

